import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, FolderOpen, PanelRight, Save, Share2, Table2 } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import LgcEditDrawer from '../components/financial/LgcEditDrawer';
import LgcTimelineHero from '../components/financial/LgcTimelineHero';
import LgcPlanDebugTable from '../components/financial/LgcPlanDebugTable';
import LgcSharePlanModal from '../components/financial/LgcSharePlanModal';
import { useAuth } from '../utils/AuthContext';
import { FinancialPlanData, FinancialPlanSummary, LgcEditorGroup } from '../types/financialPlan';
import { defaultFinancialPlanData, mergeFinancialPlanData } from '../utils/financialPlanDefaults';
import {
  deleteFinancialPlan,
  getFinancialPlan,
  listFinancialPlans,
  saveFinancialPlan,
} from '../utils/financialPlanFirestore';
import { computeRequiredIncome } from '../utils/lifetimeGoalsCalc';
import { computeGrowthTimeline } from '../utils/lifetimeGoalsTimeline';
import { buildPlanYearDebugRows } from '../utils/lgcPlanDebug';
import {
  resolvePlanAccountContributions,
  getAccountAllocationMode,
  syncPercentModeContributions,
} from '../utils/lgcAccountAllocation';
import { resolvePlanAges } from '../utils/planDates';
import { useFinancialPlanShareLiveSync } from '../hooks/useFinancialPlanShareLiveSync';
import { getFinancialPlanShareForOwner } from '../utils/financialPlanShareService';
import { editorGroupFromPinnedAge } from '../utils/lgcEditorGroups';
import '../components/pm/pm-shared.css';
import '../pages/OperationsHub.css';
import './LifetimeGoalsCalculator.css';

const DRAFT_KEY = 'lifetimeGoalsCalculator:draft:v3';
const FUNDED_THRESHOLD = 99.5;

type DraftState = {
  plan: FinancialPlanData;
  editorGroup: LgcEditorGroup;
  drawerOpen: boolean;
  planName: string;
  activePlanId: string | null;
};

const EDITOR_GROUP_IDS: LgcEditorGroup[] = ['you', 'money', 'goals', 'retirement'];

function normalizeGroup(group: unknown): LgcEditorGroup {
  if (typeof group === 'string' && EDITOR_GROUP_IDS.includes(group as LgcEditorGroup)) {
    return group as LgcEditorGroup;
  }
  return 'you';
}

function loadDraft(): DraftState | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftState> & { activeSection?: string };
    return {
      plan: mergeFinancialPlanData(parsed.plan, { preserveStored: true }),
      editorGroup: normalizeGroup(parsed.editorGroup),
      drawerOpen: parsed.drawerOpen === true,
      planName: typeof parsed.planName === 'string' ? parsed.planName : 'My Financial Plan',
      activePlanId: typeof parsed.activePlanId === 'string' ? parsed.activePlanId : null,
    };
  } catch {
    return null;
  }
}

const LifetimeGoalsCalculator: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const draft = loadDraft();
  const debugFromUrl = searchParams.get('debug') === '1';

  const [plan, setPlan] = useState<FinancialPlanData>(() => draft?.plan ?? defaultFinancialPlanData());
  const [editorGroup, setEditorGroup] = useState<LgcEditorGroup>(draft?.editorGroup ?? 'you');
  const [drawerOpen, setDrawerOpen] = useState(draft?.drawerOpen ?? false);
  const [showDebug, setShowDebug] = useState(debugFromUrl);
  const [planName, setPlanName] = useState(draft?.planName ?? 'My Financial Plan');
  const [editingName, setEditingName] = useState(false);
  const [activePlanId, setActivePlanId] = useState<string | null>(draft?.activePlanId ?? null);

  const [savedPlans, setSavedPlans] = useState<FinancialPlanSummary[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPlanMenu, setShowPlanMenu] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState(planName);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [shareSyncEnabled, setShareSyncEnabled] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const planMenuRef = useRef<HTMLDivElement | null>(null);

  const effectivePlan = useMemo(() => {
    const dated = resolvePlanAges(plan);
    return resolvePlanAccountContributions(dated);
  }, [plan]);
  const result = useMemo(() => computeRequiredIncome(effectivePlan), [effectivePlan]);
  const timeline = useMemo(() => computeGrowthTimeline(effectivePlan), [effectivePlan]);
  const debugRows = useMemo(
    () => (showDebug ? buildPlanYearDebugRows(effectivePlan, timeline) : []),
    [effectivePlan, timeline, showDebug],
  );

  useEffect(() => {
    setShowDebug(searchParams.get('debug') === '1');
  }, [searchParams]);

  useEffect(() => {
    if (!activePlanId || !user?.id) {
      setShareSyncEnabled(false);
      return;
    }
    let cancelled = false;
    void getFinancialPlanShareForOwner(activePlanId).then((share) => {
      if (!cancelled) {
        setShareSyncEnabled(share?.enabled === true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activePlanId, user?.id]);

  useFinancialPlanShareLiveSync(activePlanId, planName, effectivePlan, shareSyncEnabled);

  const updatePlan = useCallback((patch: Partial<FinancialPlanData>) => {
    setPlan((prev) => {
      const next = { ...prev, ...patch };
      if (
        getAccountAllocationMode(next) === 'percent' &&
        (patch.accounts ||
          patch.income ||
          patch.profile ||
          patch.currentFinances ||
          patch.debts ||
          patch.children ||
          patch.health ||
          patch.lifestyle ||
          patch.retirement)
      ) {
        const dated = resolvePlanAges(next);
        return {
          ...next,
          accounts: syncPercentModeContributions(next.accounts, dated),
        };
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const state: DraftState = { plan, editorGroup, drawerOpen, planName, activePlanId };
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(state));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [plan, editorGroup, drawerOpen, planName, activePlanId]);

  useEffect(() => {
    if (!user?.id) {
      setLoadingPlans(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await listFinancialPlans(user.id);
        if (!cancelled) setSavedPlans(list);
      } catch {
        if (!cancelled) setAlert({ type: 'error', message: 'Could not load saved plans.' });
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!showPlanMenu) return;
    const onDoc = (e: MouseEvent) => {
      if (planMenuRef.current && !planMenuRef.current.contains(e.target as Node)) {
        setShowPlanMenu(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showPlanMenu]);

  const handleSave = async () => {
    if (!user?.id) return;
    const name = saveNameInput.trim() || 'My Financial Plan';
    setSaving(true);
    setAlert(null);
    try {
      const id = await saveFinancialPlan(user.id, activePlanId, name, effectivePlan);
      setActivePlanId(id);
      setPlanName(name);
      const list = await listFinancialPlans(user.id);
      setSavedPlans(list);
      setShowSaveModal(false);
      setAlert({ type: 'success', message: `Saved "${name}" to your account.` });
    } catch (err) {
      console.error('saveFinancialPlan failed', err);
      setAlert({ type: 'error', message: 'Failed to save plan. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPlan = async (planId: string) => {
    if (!user?.id || planId === '') return;
    setLoadingPlan(true);
    setAlert(null);
    setShowPlanMenu(false);
    try {
      const loaded = await getFinancialPlan(user.id, planId);
      if (loaded) {
        setPlan(mergeFinancialPlanData(loaded, { preserveStored: true }));
        setPlanName(loaded.name);
        setActivePlanId(loaded.id);
        setSaveNameInput(loaded.name);
      }
    } catch {
      setAlert({ type: 'error', message: 'Could not load that plan.' });
    } finally {
      setLoadingPlan(false);
    }
  };

  const handleNewPlan = () => {
    setPlan(defaultFinancialPlanData());
    setPlanName('My Financial Plan');
    setSaveNameInput('My Financial Plan');
    setActivePlanId(null);
    setEditorGroup('you');
    setDrawerOpen(true);
    setShowPlanMenu(false);
    setAlert(null);
  };

  const handleDeletePlan = async () => {
    if (!user?.id || !activePlanId) return;
    if (!window.confirm(`Delete "${planName}"? This cannot be undone.`)) return;
    setSaving(true);
    setShowPlanMenu(false);
    try {
      await deleteFinancialPlan(user.id, activePlanId);
      const list = await listFinancialPlans(user.id);
      setSavedPlans(list);
      handleNewPlan();
      setAlert({ type: 'success', message: 'Plan deleted.' });
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete plan.' });
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (group: LgcEditorGroup) => {
    setEditorGroup(group);
    setDrawerOpen(true);
  };

  const toggleDebug = () => {
    const next = !showDebug;
    setShowDebug(next);
    setShowPlanMenu(false);
    if (next) {
      searchParams.set('debug', '1');
    } else {
      searchParams.delete('debug');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const toolbarFundedPct = timeline.retirementFundedPctOnPace;
  const expensesMet = result.phasedAffordability
    ? result.phasedAffordability.planAchievable
    : result.gapAnnual <= 0;
  const retirementFunded = toolbarFundedPct >= FUNDED_THRESHOLD;
  const currentPoint = timeline.points.find((p) => p.age === timeline.currentAge) ?? timeline.points[0];
  const surplus = currentPoint?.surplusAnnual ?? 0;
  const caption = expensesMet
    ? `Age ${timeline.currentAge} · On track`
    : `Age ${timeline.currentAge} · Short this year`;

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (loadingPlans && user?.id) {
    return (
      <div className="ops-hub lgc-hub">
        <AppHeader />
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  return (
    <div className="ops-hub lgc-hub">
      <AppHeader />

      {alert && (
        <div className={`lgc-alert lgc-alert--${alert.type}`} role="alert">
          {alert.message}
        </div>
      )}

      <div className="lgc-hub-layout">
        <div className="ops-hub-main lgc-hub-main">
          <div className="lgc-hub-toolbar">
            <div className="lgc-hub-toolbar-left">
              {editingName ? (
                <input
                  className="lgc-hub-title-input"
                  value={planName}
                  autoFocus
                  aria-label="Plan name"
                  onChange={(e) => setPlanName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="lgc-hub-title lgc-hub-title-btn"
                  onClick={() => setEditingName(true)}
                >
                  {planName}
                </button>
              )}
              <div className="lgc-hub-stats">
                <span
                  className={`ops-stat ${expensesMet ? 'ops-stat-done' : 'ops-stat-overdue'}`}
                  title={
                    expensesMet
                      ? 'Income covers expenses every year'
                      : 'Income falls short in at least one year'
                  }
                >
                  Expenses {expensesMet ? 'on track' : 'short'}
                </span>
                <span
                  className={`ops-stat ${retirementFunded ? 'ops-stat-done' : 'ops-stat-overdue'}`}
                  title={`Retirement ${Math.round(toolbarFundedPct)}% funded`}
                >
                  Retirement {Math.round(toolbarFundedPct)}%
                </span>
              </div>
            </div>
            <div className="lgc-hub-toolbar-right">
              <div className={`pm-dropdown-host${showPlanMenu ? ' pm-dropdown-host--open' : ''}`} ref={planMenuRef}>
                <button
                  type="button"
                  className="ops-view-as-btn"
                  aria-haspopup="menu"
                  aria-expanded={showPlanMenu}
                  onClick={() => setShowPlanMenu((v) => !v)}
                >
                  Plan <ChevronDown size={12} />
                </button>
                {showPlanMenu && (
                  <div className="pm-dropdown lgc-plan-menu" role="menu">
                    {savedPlans.length > 0 && (
                      <>
                        {savedPlans.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="pm-dropdown-item"
                            role="menuitem"
                            disabled={loadingPlan}
                            onClick={() => void handleLoadPlan(p.id)}
                          >
                            {p.name}
                            {p.id === activePlanId ? ' · current' : ''}
                          </button>
                        ))}
                        <div className="lgc-plan-menu-sep" />
                      </>
                    )}
                    <button type="button" className="pm-dropdown-item" role="menuitem" onClick={handleNewPlan}>
                      New plan
                    </button>
                    {activePlanId && (
                      <button
                        type="button"
                        className="pm-dropdown-item"
                        role="menuitem"
                        disabled={saving}
                        onClick={() => void handleDeletePlan()}
                      >
                        Delete plan
                      </button>
                    )}
                    <button type="button" className="pm-dropdown-item" role="menuitem" onClick={toggleDebug}>
                      <Table2 size={13} />
                      {showDebug ? 'Hide year-by-year' : 'Year-by-year'}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="ops-view-as-btn"
                onClick={() => setShowShareModal(true)}
              >
                <Share2 size={13} /> Share
              </button>
              <button
                type="button"
                className={`ops-view-as-btn${drawerOpen ? ' is-active' : ''}`}
                onClick={() => setDrawerOpen((v) => !v)}
              >
                <PanelRight size={13} /> Edit plan
              </button>
              <button
                type="button"
                className="ops-new-btn"
                disabled={saving || !user?.id}
                onClick={() => {
                  setSaveNameInput(planName);
                  setShowSaveModal(true);
                }}
              >
                <Save size={13} />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="lgc-timeline-page">
            <LgcTimelineHero
              timeline={timeline}
              fullPage
              onPinEdit={(age) => openEditor(editorGroupFromPinnedAge(timeline, age))}
            />
            <p className="lgc-graph-caption">
              {caption}
              {surplus !== 0 && (
                <>
                  {' '}
                  · {surplus >= 0 ? 'Surplus' : 'Gap'} $
                  {Math.abs(Math.round(surplus)).toLocaleString()}/yr
                </>
              )}
            </p>
          </div>

          {showDebug && (
            <div className="lgc-content-scroll lgc-content-scroll--full lgc-debug-panel">
              <LgcPlanDebugTable timeline={timeline} rows={debugRows} />
            </div>
          )}
        </div>
      </div>

      <LgcEditDrawer
        open={drawerOpen}
        activeGroup={editorGroup}
        plan={effectivePlan}
        sourcePlan={plan}
        onClose={() => setDrawerOpen(false)}
        onSelectGroup={setEditorGroup}
        onUpdate={updatePlan}
      />

      {showShareModal && (
        <LgcSharePlanModal
          planId={activePlanId}
          planName={planName}
          planData={effectivePlan}
          onClose={() => setShowShareModal(false)}
          onShareActiveChange={setShareSyncEnabled}
        />
      )}

      {showSaveModal && (
        <div
          className="ops-modal-overlay"
          onClick={() => !saving && setShowSaveModal(false)}
          role="presentation"
        >
          <div className="ops-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="lgc-save-title">
            <div className="ops-modal-header">
              <h3 id="lgc-save-title">Save Financial Plan</h3>
              <button
                type="button"
                className="ops-modal-close"
                onClick={() => setShowSaveModal(false)}
                aria-label="Close"
                disabled={saving}
              >
                ×
              </button>
            </div>
            <div className="ops-modal-body">
              <label htmlFor="lgc-plan-name" className="lgc-field-label">
                Plan name
              </label>
              <input
                id="lgc-plan-name"
                className="ops-modal-input ops-modal-input-title"
                value={saveNameInput}
                onChange={(e) => setSaveNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <p className="lgc-hint" style={{ textTransform: 'none', fontWeight: 500 }}>
                <FolderOpen size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Stored securely in your account
              </p>
            </div>
            <div className="ops-modal-footer">
              <button type="button" className="pm-btn" onClick={() => setShowSaveModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="pm-btn pm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : activePlanId ? 'Update plan' : 'Save plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifetimeGoalsCalculator;
